import { SyntheticEvent, useState } from "react";
import { Bar } from "react-chartjs-2";
import { tabPanelClasses, TabContext, TabPanel } from "@mui/lab";
import { CardContent, Tab, Box, Typography as T, Tooltip } from "@mui/material";
import { ChartOptions } from "chart.js";
import { SiteDetails, Measurement } from "catalog/types/interfaces/conceptResponse";
import ContainedTabs from "~/components/ContainedTabs";
import FlatCard from "~/components/FlatCard";
import { COLOR_PALETTE } from "~/customer/chartJS/colors";
import InfoIcon from "~/pages/catalog/components/InfoIcon";
import { DEFAULT_OPTS, isHighCardinalityStackedBar } from "~/pages/catalog/utils/chart";
import { merge } from "chart.js/helpers";

const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

const MAX_LABEL_CHARS = 16;

const CHART_OPTS: ChartOptions<"bar"> = merge<ChartOptions<"bar">, ChartOptions<"bar">>(
  {
    indexAxis: "y",
    scales: {
      x: {
        grid: { display: false },
        stacked: true,
        title: {
          display: true,
          text: "Participant Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
      y: {
        stacked: true,
        ticks: {
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            return label
              .slice(0, MAX_LABEL_CHARS)
              .concat(label.length > MAX_LABEL_CHARS ? "..." : "");
          },
        },
      },
    },
    plugins: {
      colors: {
        forceOverride: true,
      },
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        displayColors: false,
      },
    },
  },
  DEFAULT_OPTS
);

const getChartData = (type: "gender_counts" | "race_counts", data: Measurement) => {
  /**
   * If comparing between 'Negative' and 'Positive' values,
   * ensures 'Positive' is at index 0
   */
  if (type === "gender_counts" && data.values.findIndex((v) => v.value === "Negative") === 0) {
    data.values.reverse();
  }

  /**
   * Genders/Races with zero values are omitted in the API. Need to first collect all
   * possible label values so we can pad the dataset appropriately when there are gaps
   */
  const datasetLabels = data.values.reduce<string[]>((acc, value) => {
    const tmp: string[] = [];
    value?.[type]?.forEach((b) => {
      if (acc.indexOf(b.concept_name) === -1) {
        tmp.push(b.concept_name);
      }
    });
    return [...acc, ...tmp];
  }, []);

  /**
   * For each label, pull the corresponding count for each status code,
   * padding where data is missing for a particular label
   */
  const datasets = datasetLabels.map((label, i) => {
    const counts: number[] = [];
    data.values.forEach((value) => {
      const target = value?.[type]?.find((b) => b.concept_name === label);
      if (target) {
        counts.push(target.participant_count);
      } else {
        counts.push(0);
      }
    });

    return {
      label,
      data: counts,
      backgroundColor: COLOR_PALETTE?.[i] ?? "#ccc",
    };
  });

  return {
    labels: data.values.map(({ value }) => value),
    datasets,
  };
};

const MeasurementChart = (props: {
  data: SiteDetails;
  measurementName: string;
  description?: string;
}) => {
  const { measurementName } = props;
  const { measurement_breakdown } = props.data;
  const [chartTab, setChartTab] = useState<"race" | "gender">("gender");

  const measurement = measurement_breakdown?.find(
    (item) => item.measurement_name === measurementName
  );

  if (!measurement) {
    return null;
  }

  const raceData = getChartData("race_counts", measurement);
  const genderData = getChartData("gender_counts", measurement);
  const isHighCardinality = isHighCardinalityStackedBar(raceData, genderData);

  return (
    <FlatCard
      sx={{
        // Use full width if stacked bar chart has many labels and/or values per label
        gridColumn: isHighCardinality ? "span 2" : "span 1",
        [`.${tabPanelClasses.root}`]: { p: 1 },
      }}
    >
      <CardContent>
        <TabContext value={chartTab}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5} pb={0.5}>
            <T
              variant="body1"
              fontWeight={600}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {measurement.measurement_name}
            </T>
            {props.description && (
              <Tooltip arrow title={props.description}>
                <InfoIcon />
              </Tooltip>
            )}
          </Box>
          <Box display="flex" justifyContent="center" mt={1}>
            <ContainedTabs
              value={chartTab}
              onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
            >
              <Tab label="Gender" value="gender" />
              <Tab label="Race" value="race" />
            </ContainedTabs>
          </Box>
          <TabPanel value="race" className="chart-container">
            <Bar data={raceData} options={CHART_OPTS} />
          </TabPanel>
          <TabPanel value="gender" className="chart-container">
            <Bar data={genderData} options={CHART_OPTS} />
          </TabPanel>
        </TabContext>
      </CardContent>
    </FlatCard>
  );
};

export default MeasurementChart;
